import apiClient from '../../../api-client';
import {
  OrderCreateFullRequest,
  PreProcessPrices,
} from '../../interfaces/Orders';
import { ProductForOrder } from '../../utils/mappers/ProductMapper';

export class OrderService {
  public async getOrders(companyId: number, userId: number) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders/user/${userId}`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching userOrders: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async createFullOrder(
    companyId: number,
    orderData: OrderCreateFullRequest
  ) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders/create`,
        {
          ...orderData,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating order: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async createOrder(
    companyId: number,
    deliveryAddress: object,
    invoiceAddress: object,
    total: number,
    type: string
  ) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders`,
        {
          deliveryaddress: deliveryAddress,
          invoiceaddress: invoiceAddress,
          total: total,
          type: type,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating order: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async createOrderProducts(
    companyId: number,
    userId: number,
    products: object[],
    orderId: number,
    bulk: boolean = false
  ) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders/${orderId}/products`,
        {
          userId: userId,
          products: products,
          bulk: bulk,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating orderproducts: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async createOrderPayment(
    companyId: number,
    orderId: number,
    type: string,
    amount: number,
    coupon: string | null
  ) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders/${orderId}/payment`,
        {
          type: type,
          bedrag: amount,
          coupon: coupon,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating orderpayment: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async sendShopNotification(
    id: number,
    companyId: number,
    products: any
  ) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/${companyId}/order/${id}/send`,
        {
          products: products,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating orderpayment: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async checkOrderBeforeProcessing(
    buyerProfile: boolean,
    cyod: boolean,
    products: any[],
    prices: PreProcessPrices,
    companyId: number
  ) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders/intent`,
        {
          buyerProfile: buyerProfile,
          cyod: cyod,
          products: products,
          prices: prices,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error prechecking order: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async paymentIntent(
    companyId: number,
    currency: string,
    items: ProductForOrder[]
  ) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders/payment/intent`,
        {
          items: items,
          currency: currency,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating orderpayment: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async cancelOrder(companyId: number, orderId: number): Promise<void> {
    try {
      await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders/${orderId}/cancel`
      );
    } catch (error: any) {
      throw new Error(
        `Error canceling order: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async cancelLastOrderForUser(companyId: number): Promise<void> {
    try {
      await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/orders/user/cancel`
      );
    } catch (error: any) {
      throw new Error(
        `Error canceling last order: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }
}
